import {
  createDiscountRule as publicCreateDiscountRule,
  getDiscountRule as publicGetDiscountRule,
  updateDiscountRule as publicUpdateDiscountRule,
  deleteDiscountRule as publicDeleteDiscountRule,
  queryDiscountRules as publicQueryDiscountRules,
  typedQueryDiscountRules as publicTypedQueryDiscountRules,
  bulkUpdateDiscountRuleTags as publicBulkUpdateDiscountRuleTags,
  bulkUpdateDiscountRuleTagsByFilter as publicBulkUpdateDiscountRuleTagsByFilter,
} from './ecom-discounts-v1-discount-rule-discount-rules.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  DiscountRuleQuery,
  DiscountRulesQueryBuilder,
  typedQueryDiscountRules as universalTypedQueryDiscountRules,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
import { onDiscountRuleCreated as publicOnDiscountRuleCreated } from './ecom-discounts-v1-discount-rule-discount-rules.public.js';
import { onDiscountRuleDeleted as publicOnDiscountRuleDeleted } from './ecom-discounts-v1-discount-rule-discount-rules.public.js';
import { onDiscountRuleUpdated as publicOnDiscountRuleUpdated } from './ecom-discounts-v1-discount-rule-discount-rules.public.js';

function customQueryDiscountRules(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: () => publicQueryDiscountRules(httpClient)(),
    typedQueryFunction: (query: DiscountRuleQuery) =>
      publicTypedQueryDiscountRules(httpClient)(query),
    hasOptionsParameter: false,
  });

  function overloadedQuery(): DiscountRulesQueryBuilder;
  function overloadedQuery(
    query: DiscountRuleQuery
  ): ReturnType<typeof universalTypedQueryDiscountRules>;
  function overloadedQuery(query?: DiscountRuleQuery): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createDiscountRule: MaybeContext<
  BuildRESTFunction<typeof publicCreateDiscountRule> &
    typeof publicCreateDiscountRule
> = /*#__PURE__*/ createRESTModule(publicCreateDiscountRule);
export const getDiscountRule: MaybeContext<
  BuildRESTFunction<typeof publicGetDiscountRule> & typeof publicGetDiscountRule
> = /*#__PURE__*/ createRESTModule(publicGetDiscountRule);
export const updateDiscountRule: MaybeContext<
  BuildRESTFunction<typeof publicUpdateDiscountRule> &
    typeof publicUpdateDiscountRule
> = /*#__PURE__*/ createRESTModule(publicUpdateDiscountRule);
export const deleteDiscountRule: MaybeContext<
  BuildRESTFunction<typeof publicDeleteDiscountRule> &
    typeof publicDeleteDiscountRule
> = /*#__PURE__*/ createRESTModule(publicDeleteDiscountRule);
export const bulkUpdateDiscountRuleTags: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateDiscountRuleTags> &
    typeof publicBulkUpdateDiscountRuleTags
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateDiscountRuleTags);
export const bulkUpdateDiscountRuleTagsByFilter: MaybeContext<
  BuildRESTFunction<typeof publicBulkUpdateDiscountRuleTagsByFilter> &
    typeof publicBulkUpdateDiscountRuleTagsByFilter
> = /*#__PURE__*/ createRESTModule(publicBulkUpdateDiscountRuleTagsByFilter);
export const queryDiscountRules: MaybeContext<
  BuildRESTFunction<typeof customQueryDiscountRules> &
    typeof customQueryDiscountRules
> = /*#__PURE__*/ createRESTModule(customQueryDiscountRules);
/**
 * Triggered when a discount rule is created.
 */
export const onDiscountRuleCreated: BuildEventDefinition<
  typeof publicOnDiscountRuleCreated
> &
  typeof publicOnDiscountRuleCreated = createEventModule(
  publicOnDiscountRuleCreated
);
/**
 * Triggered when a discount rule is deleted.
 */
export const onDiscountRuleDeleted: BuildEventDefinition<
  typeof publicOnDiscountRuleDeleted
> &
  typeof publicOnDiscountRuleDeleted = createEventModule(
  publicOnDiscountRuleDeleted
);
/**
 * Triggered when a discount rule is updated.
 */
export const onDiscountRuleUpdated: BuildEventDefinition<
  typeof publicOnDiscountRuleUpdated
> &
  typeof publicOnDiscountRuleUpdated = createEventModule(
  publicOnDiscountRuleUpdated
);

export {
  ScopeType,
  EligibilityType,
  TriggerType,
  DayOfWeek,
  Type,
  DiscountType,
  Status,
  AppliedSubjectType,
  SortOrder,
  WebhookIdentityType,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
export {
  DiscountRule,
  DiscountTrigger,
  DiscountTriggerTriggerOneOf,
  Scope,
  ScopeScopeItemsOneOf,
  CatalogItemFilter,
  CustomFilter,
  And,
  Or,
  Custom,
  SubtotalRange,
  ItemQuantityRange,
  CustomerEligibility,
  CustomerEligibilityOptionsOneOf,
  ContactSegments,
  ContactTags,
  TagReference,
  IndividualMembers,
  ActiveTimeInfo,
  Recurrence,
  TimeOfDay,
  TimeWindow,
  WeeklySchedule,
  Discounts,
  Discount,
  DiscountDiscountOneOf,
  SpecificItemsInfo,
  QuantityBased,
  DiscountConfig,
  BuyXGetYInfo,
  CustomerBuy,
  CustomerBuyConditionOneOf,
  CustomerGet,
  DiscountSettings,
  ExtendedFields,
  Tags,
  TagList,
  TagsModified,
  CreateDiscountRuleRequest,
  CreateDiscountRuleResponse,
  GetDiscountRuleRequest,
  GetDiscountRuleResponse,
  UpdateDiscountRuleRequest,
  UpdateDiscountRuleResponse,
  DeleteDiscountRuleRequest,
  DeleteDiscountRuleResponse,
  QueryDiscountRulesRequest,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  Sorting,
  PlatformPaging,
  CursorPaging,
  QueryDiscountRulesResponse,
  PlatformPagingMetadata,
  Cursors,
  GetAppliedDiscountsRequest,
  LineItem,
  CatalogReference,
  BuyerInfo,
  ShippingInfo,
  Address,
  GetAppliedDiscountsResponse,
  AppliedDiscount,
  AppliedDiscountRule,
  DiscountRuleName,
  MultiCurrencyPrice,
  ItemCombination,
  ItemCombinationLineItem,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  Empty,
  DiscountRuleUsageLimitReached,
  BulkUpdateDiscountRuleTagsRequest,
  BulkUpdateDiscountRuleTagsResponse,
  ItemMetadata,
  ApplicationError,
  BulkUpdateDiscountRuleTagsResult,
  BulkActionMetadata,
  BulkUpdateDiscountRuleTagsByFilterRequest,
  BulkUpdateDiscountRuleTagsByFilterResponse,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  DiscountRuleCreatedEnvelope,
  DiscountRuleDeletedEnvelope,
  DiscountRuleUpdatedEnvelope,
  UpdateDiscountRule,
  DiscountRulesQueryResult,
  DiscountRulesQueryBuilder,
  DiscountRuleQuerySpec,
  BulkUpdateDiscountRuleTagsOptions,
  BulkUpdateDiscountRuleTagsByFilterOptions,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
export { utils } from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
export {
  ScopeTypeWithLiterals,
  EligibilityTypeWithLiterals,
  TriggerTypeWithLiterals,
  DayOfWeekWithLiterals,
  TypeWithLiterals,
  DiscountTypeWithLiterals,
  StatusWithLiterals,
  AppliedSubjectTypeWithLiterals,
  SortOrderWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CreateDiscountRuleApplicationErrors,
  CreateDiscountRuleValidationErrors,
  BulkUpdateDiscountRuleTagsApplicationErrors,
  BulkUpdateDiscountRuleTagsByFilterApplicationErrors,
  CommonQueryWithEntityContext,
  DiscountRuleQuery,
} from './ecom-discounts-v1-discount-rule-discount-rules.universal.js';
